import { useLayoutEffect, useState } from "react";
import { useBreakpointValue } from "@gluestack-ui/themed";
import { CardResource } from "@repo/core/services/resources/card";
import { apiInstance } from "@repo/core/services/instances/api";
import { IdentityTypeInputProps } from "./types";
import useToast from "../../core/Toast";
import { AxiosError } from "axios";
import { ClientApiResource } from "@repo/core/services/resources/client-api";
import { ConsultCardsSchemaResponseDTO } from "@repo/core/services/entities/ClientApi";
import { useDeviceType } from "@repo/core/hooks/useDeviceType";
import { useDebounceCallback } from "usehooks-ts";
import { isValidCard, isValidCPF } from "@repo/core/helpers/zod.helpers";

const cardResource = new CardResource(apiInstance);
const clientApiResource = new ClientApiResource(apiInstance);

export const useIdentityTypeInputController = ({
  control,
  onSelectProduct,
  selectedInputType,
  shouldFetchOnCardSubmit,
  onFetchCard,
  onFetchProducts,
  onFetchCardFinish,
  onFetchProductsFinish,
  onChangeCard,
  onChangeCPF
}: IdentityTypeInputProps) => {
  const { error } = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const { isMobile: isMobileDevice } = useDeviceType();

  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);

  const [products, setProducts] = useState<
    ConsultCardsSchemaResponseDTO["lstProdutos"]
  >([]);
  const [selectedProduct, setSelectedProduct] = useState<null | string>(null);

  const handleFetchDataFromCardInput = async () => {
    const cardNumber = control._getWatch("card");
    if (!shouldFetchOnCardSubmit) return;
    if (cardNumber.length < 16) return;

    const { data: profileCards } =
      await cardResource.consultProfileWithCardNumber(cardNumber);

    const card = profileCards.lstPlasticos.find(
      (card) => card.strPlastico?.trim() === cardNumber
    );

    if (card) {
      const { data } = await clientApiResource.consultCards(
        card.strCpfPortador
      );

      return data;
    }
    return null;
  };

  const handleFetchDataFromCpfInput = async () => {
    const cpf = control._getWatch("cpf");
    if (cpf.length < 11) return;

    const { data } = await clientApiResource.consultCards(cpf);
    if (data) {
      return data;
    }
    return null;
  };

  const fetchCardData = async (
    product: ConsultCardsSchemaResponseDTO["lstProdutos"][0],
    lstProdutos: ConsultCardsSchemaResponseDTO["lstProdutos"],
    productId?: string
  ) => {
    const { data } = await cardResource.consultData(
      product.strProduto,
      product.lngCartao
    );
    if (data) {
      setSelectedProduct(productId || "0");
      setProducts(lstProdutos);
      return data;
    }
  };

  const handleFindSameCardAndSelectProduct = async (
    lstProdutos: ConsultCardsSchemaResponseDTO["lstProdutos"]
  ) => {
    const cardNumber = control._getWatch("card");
    const product =
      lstProdutos.find(
        (product) =>
          product.strPlastico.trim().slice(-4) === cardNumber.slice(-4)
      ) || lstProdutos[0];

    if (product) {
      const productInfo = await fetchCardData(product, lstProdutos);
      return productInfo;
    }
  };

  const handleFetchProductForCard = async () => {
    try {
      onFetchCard?.();
      const products = await handleFetchDataFromCardInput();

      if (!products) return;

      const productInfo = await handleFindSameCardAndSelectProduct(
        products.lstProdutos
      );
      if (productInfo) {
        onFetchCardFinish?.(productInfo);
      }
    } catch (err) {
      if (err instanceof AxiosError && err.response?.data?.strMensagem) {
        error(err.response.data.strMensagem);
        return;
      }
      error(
        "Cartão não encontrado, por favor verifique o cartão e tente novamente."
      );
    }
  };

  const handleFetchProductsForCPF = async () => {
    if (isLoadingProducts) return;

    onFetchProducts?.();

    setIsLoadingProducts(true);
    try {
      const products = await handleFetchDataFromCpfInput();

      if (!products) return;

      if (products.lstProdutos.length === 1) {
        const productInfo = await handleFindSameCardAndSelectProduct(
          products.lstProdutos
        );
        if (productInfo) {
          onSelectProduct(productInfo);
        }
      } else {
        setProducts(products.lstProdutos);
      }
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response?.data?.strMensagem) {
        error(err.response.data.strMensagem);
        return;
      }
      error("CPF não encontrado, por favor verifique o CPF e tente novamente.");
    } finally {
      onFetchProductsFinish?.();
      setIsLoadingProducts(false);
    }
  };

  const handleSelectProduct = async (id: string) => {
    if (id === selectedProduct) {
      setSelectedProduct(null);
      return;
    }
    setIsLoadingProduct(true);

    setSelectedProduct(id);

    const product = products[Number(id)];

    const productInfo = await fetchCardData(product, products, id);

    if (productInfo) {
      onSelectProduct?.(productInfo);
    }
    setIsLoadingProduct(false);
  };

  const clearInputAndData = () => {
    setSelectedProduct(null);
    setProducts([]);
    control._reset();
  };

  useLayoutEffect(() => {
    clearInputAndData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInputType]);

  const handleOnChangeCpf = (value: string) => {
    onChangeCPF?.(value);
    const isValid = isValidCPF(value);
    if (!value) {
      clearInputAndData();
    }
    if (isValid) {
      handleFetchProductsForCPF();
    }
  };

  const handleOnChangeCard = (value: string) => {
    onChangeCard?.(value);
    const isValid = isValidCard(value);
    if (!value) {
      clearInputAndData();
    }
    if (isValid) {
      handleFetchProductForCard();
    }
  };

  const debouncedHandleOnChangeCpf = useDebounceCallback(
    handleOnChangeCpf,
    500
  );

  const debouncedHandleOnChangeCard = useDebounceCallback(
    handleOnChangeCard,
    500
  );

  return {
    isMobile,
    isMobileDevice,
    handleFetchProductsForCPF,
    handleFetchProductForCard,
    handleSelectProduct,
    products,
    selectedProduct,
    isLoadingProducts,
    isLoadingProduct,
    debouncedHandleOnChangeCpf,
    debouncedHandleOnChangeCard
  };
};
